import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DepositSettingsForm } from "../components/DepositSettingsForm";
import { ErrorAlert } from "../components/ErrorAlert";
import { useTRPC } from "../lib/trpc";

/**
 * The platform default for deposits: the presets every storefront's Deposit offers and the least
 * and most a shopper may deposit, until its tenant sets its own under Storefront → Payments →
 * Deposits. Platform admins only. The design's settings until the first save; never cleared.
 */
export function PlatformDepositsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.deposit.get.queryOptions();
  const query = useQuery(options);
  const save = useMutation(
    trpc.platform.deposit.set.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(options.queryKey, saved);
        void queryClient.invalidateQueries({ queryKey: options.queryKey });
        toast.success("Platform deposit settings saved.");
      },
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Deposits</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What a shopper may deposit, at face value, in every storefront whose tenant has not set
          its own: the presets Deposit offers and the least and most one deposit can be.
        </p>
      </div>
      {query.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {query.error && <ErrorAlert message={query.error.message} />}
      {query.data && (
        <Card>
          <CardHeader>
            <CardTitle>Platform deposit settings</CardTitle>
            <CardDescription>
              Checkout refuses an amount outside them. A change reaches storefronts within a minute.
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-4">
            <DepositSettingsForm
              key={JSON.stringify(query.data.deposit)}
              deposit={query.data.deposit}
              canEdit
              saving={save.isPending}
              onSave={(deposit) => save.mutateAsync({ deposit })}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
